import React, { useRef, useState } from 'react'
import { IEvent } from '../../store/events/interfaces'
import { Badge, Button, Card, Overlay, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { RootState } from '../../store'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { EventAge } from './EventAge'
import { EventOccupation } from './EventOccupation'
import { useNavigate } from 'react-router-dom'
import { isTabletOrSmallDesktopQuery } from '../../utils'
import { useMediaQuery } from 'react-responsive'
import { RegisterButton } from '../shared/RegisterButton'
import { WorkflowStatusBadge } from '../shared/WorkflowStatusBadge'
import { useSelector } from 'react-redux'

const InfoBadge = (props: {
  icons: IconProp[]
  variant: string
  tooltip: string
  show: boolean
  hasRegistration?: boolean
  setShow: (prevState: boolean) => void
}) => {
  const target = useRef<HTMLButtonElement>(null)
  const { icons, tooltip, show, setShow, variant, hasRegistration } = props
  const isTabletOrSmallDesktop = useMediaQuery(isTabletOrSmallDesktopQuery)

  return (
    <>
      {isTabletOrSmallDesktop ? (
        <>
          <button
            className={`btn btn-sm btn-${hasRegistration ? 'outline-light' : variant
              } mr-2 info-badge`}
            ref={target}
            onClick={() => setShow(!show)}
            onBlur={() => (show ? setShow(!show) : null)}
            aria-label='Anzeige des zugehörigen Tooltips'
          >
            {
              icons.map(i =>
                <FontAwesomeIcon
                  icon={i}
                  aria-label={tooltip}
                  data-toggle='tooltip'
                  role='tooltip'
                  aria-hidden='true'
                  aria-live='polite'
                  tabIndex={0}
                  key={`${i}`}
                />)
            }

          </button>
          {target && target.current && (
            <Overlay target={target.current} show={show} placement='top'>
              {(props: any) => <Tooltip {...props}>{tooltip}</Tooltip>}
            </Overlay>
          )}
        </>
      ) : (
        <>
          <OverlayTrigger
            placement='top'
            overlay={
              <Tooltip id='tooltip-top'>{tooltip}</Tooltip>
            }
            trigger={['hover', 'focus']}
          >
            <span aria-label={tooltip}
              className='mr-1'
              data-toggle='tooltip'
              role='tooltip'
              aria-hidden='true'
              aria-live='polite'>
              {
                icons.map(i =>
                  <FontAwesomeIcon
                    icon={i}
                    tabIndex={0}
                    key={`${i}`}
                  />)
              }
            </span>
          </OverlayTrigger>{' '}
        </>
      )}
    </>
  )
}

type InfoBadgeData = {
  icons: IconProp[]
  variant: string
  tooltip: string
  predicate: (event: IEvent) => boolean
}

const INFO_BADGES: InfoBadgeData[] = [
  {
    icons: ['ban'],
    variant: 'outline-dark',
    tooltip: 'Abgesagt',
    predicate: event => event.canceled,
  },
  {
    icons: ['star'],
    variant: 'outline-dark',
    tooltip: 'Nur für Sonderkunden',
    predicate: event => event.is_only_for_special_customers,
  },
  {
    icons: ['external-link-square-alt'],
    variant: 'outline-dark',
    tooltip: 'Externe Anmeldung',
    predicate: event => event.has_external_registration,
  },
  {
    icons: ['wheelchair'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Gehbeeinträchtigte',
    predicate: event => event.supported_disabilities.includes('wheelchair'),
  },
  {
    icons: ['low-vision'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Sehbbeeinträchtigte',
    predicate: event => event.supported_disabilities.includes('blind'),
  },
  {
    icons: ['deaf'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Hörbeeinträchtigte',
    predicate: event => event.supported_disabilities.includes('deaf'),
  },
  {
    icons: ['user-plus'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Geistigbeeinträchtigte',
    predicate: event => event.supported_disabilities.includes('intellectual'),
  },
  {
    icons: ['book'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Lernbeeinträchtigte',
    predicate: event => event.supported_disabilities.includes('learning'),
  },
  {
    icons: ['users'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Entwicklungsbeeinträchtigte',
    predicate: event => event.supported_disabilities.includes('developmental'),
  },
  {
    icons: ['male'],
    variant: 'outline-dark',
    tooltip: 'Unterstützt Körperlichbeeinträchtigte',
    predicate: event => event.supported_disabilities.includes('physical'),
  },
  {
    icons: ['file'],
    variant: 'outline-dark',
    tooltip: 'Hat Anhänge',
    predicate: event => event.attachments.length > 0,
  },
  {
    icons: ['user-friends'],
    variant: 'outline-dark',
    tooltip: 'Nur für Erwachsene',
    predicate: event => event.is_only_for_adults,
  },
  {
    icons: ['venus'],
    variant: 'outline-danger',
    tooltip: 'Nur für Mädchen',
    predicate: event => event.allowed_genders === 'w',
  },
  {
    icons: ['mars'],
    variant: 'outline-primary',
    tooltip: 'Nur für Jungs',
    predicate: event => event.allowed_genders === 'm',
  },
  {
    icons: ['neuter'],
    variant: 'outline-secondary',
    tooltip: 'Nur für Diverse',
    predicate: event => event.allowed_genders === 'd',
  },
  {
    icons: ['neuter', 'mars'],
    variant: 'outline-secondary',
    tooltip: 'Nur für Diverse und Jungs',
    predicate: event => event.allowed_genders === 'md',
  },
  {
    icons: ['neuter', 'venus'],
    variant: 'outline-secondary',
    tooltip: 'Nur für Diverse und Mädchen',
    predicate: event => event.allowed_genders === 'wd',
  },
  {
    icons: ['mars', 'venus'],
    variant: 'outline-secondary',
    tooltip: 'Nur für Jungs und Mädchen',
    predicate: event => event.allowed_genders === 'mw',
  },
]

export const InfoBadges = (props: {
  event: IEvent
  hasRegistration?: boolean
}) => {
  const { event, hasRegistration } = props
  const requiredBadges = INFO_BADGES.filter(badgeData =>
    badgeData.predicate(event),
  )

  // add tags (Kategorien as a Badge)
  if (event.tags && event.tags.length > 1) {
    requiredBadges.push({
      icons: ['tags'],
      variant: 'outline-dark',
      tooltip: event.tags.join(", "),
      predicate: () => true,
    },)
  }

  const [shows, setShows] = useState<boolean[]>(
    new Array(requiredBadges.length).fill(false),
  )

  const setShow = (idx: number) => (value: boolean) =>
    setShows(shows.map((show: boolean, key: number) => key === idx && value))

  return (
    <>
      {requiredBadges.map((badgeData, key) => (
        <InfoBadge
          hasRegistration={hasRegistration}
          key={key}
          show={shows[key]}
          setShow={setShow(key)}
          {...badgeData}
        />
      ))}
    </>
  )
}

export const PriceBadge = (props: {
  event: IEvent
}) => {
  const { event } = props
  const priceText = event.has_external_payment ? event.price + "€ externe Bezahlung" : event.price + "€";
  const positivePriceValue = event.price > 0;

  return (
    <>
      {positivePriceValue && (
        <>
          <Badge className="badge-pill badge-info">{priceText}</Badge>
        </>
      )}
    </>
  );
}

const EventCard = (props: { event: IEvent; hasRegistration: boolean }) => {
  const navigate = useNavigate()
  const { event, hasRegistration } = props
  const { data: program } = useSelector((state: RootState) => state.program)
  const {
    testMode,
  } = useSelector((state: RootState) => state.institution)
  const isExternal = event.has_external_registration;

  return (
    <Card
      className={`${hasRegistration ? 'border-success' : 'border-dark'} h-100`}
      id={'event-' + event.relative_id}
      tabIndex={0}
    >
      {!!event.cover_photo && program.enable_preview_images && (
        <Card.Img
          variant='top'
          src={event.cover_photo.medium}
          alt={
            event.cover_photo.caption
              ? event.cover_photo.caption
              : `Vorschaubild der Veranstaltung ${event.name}`
          }
        />
      )}
      {testMode && (
        <WorkflowStatusBadge event={event}></WorkflowStatusBadge>
      )}
      <Card.Header
        className={`${hasRegistration && 'dark-success text-white'}`}
      >
        <strong>
          {event.name} (#{event.relative_id})
        </strong>
        <br />
        <small>
          {event.duration_summary
            ? event.duration_summary
            : 'Veranstaltung ohne Zeitraum'}
        </small>
        <br />
        <InfoBadges event={event} hasRegistration={hasRegistration} />
      </Card.Header>
      <Card.Body>
        <strong>
          {program.requires_family_data
            ? 'Altersempfehlung'
            : 'Altersbeschränkung'}
        </strong>
        <p>
          <EventAge
            event={event}
            isFamilyRequired={program.requires_family_data}
          />
        </p>
        <hr />
        {program.booking_visibility !== 'never' &&
          !program.hide_event_occupation ? (
          <>
            <strong>Belegung</strong>
            <div className='mt-3'>
              <EventOccupation event={event} isModal={false} />
            </div>
            <hr />
          </>
        ) : (
          <></>
        )}
        <>
          <p><strong>Kosten</strong></p>
          {event.price_categories.length > 0 ? (
            <>
              <ul className='list-unstyled'>
                <li>
                  {event.display_price}
                </li>
                {event.price_categories.map((item, index) =>
                  <li key={index}>{item.name}: {item.price.toFixed(2,)}&nbsp;€</li>
                )}
              </ul>
              {event.has_external_payment &&
                <>
                  <Badge variant='warning' tabIndex={0}>
                    Achtung
                  </Badge>{' '}
                  <small className="text-muted">Bezahlung wird direkt über den Veranstalter geregelt!</small>
                </>
              }
            </>
          ) : (
            <>
              {event.price ? (
                <>
                  {`${event.price.toFixed(2)} €`}
                  {event.has_external_payment &&
                    <>
                      <br />
                      <Badge variant='warning' tabIndex={0}>
                        Achtung
                      </Badge>{' '}
                      <small className="text-muted">Bezahlung wird direkt über den Veranstalter geregelt!</small>
                    </>
                  }
                </>
              ) : ('Kostenlos')}
            </>
          )
          }
        </>
      </Card.Body>
      <Card.Footer className='d-flex justify-content-between flex-column'>
        <RegisterButton event={event} editable={true} extraClasses={"mb-1"}/>
        {isExternal ? (
          <Button
            variant='outline-dark'
            onClick={() => {
              navigate(`/veranstaltungen/${event.event_id}`, { replace: true })
            }}
            className='mobile-btn'
            aria-label='Veranstaltungsdetails'
          >
            Mehr Erfahren
          </Button>
        ) : (
          <Button
            variant='outline-dark'
            onClick={() => {
              navigate(`/veranstaltungen/${event.event_id}`, { replace: true })
            }}
            className='mobile-btn'
            aria-label='Veranstaltungsdetails'
          >
            <FontAwesomeIcon icon='info-circle' role='presentation' /> Details
          </Button>
        )}
      </Card.Footer>
    </Card>
  )
}

export default EventCard
